import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useData } from "@/lib/data";
import { groupByProduct } from "@/lib/koli";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import SizeMatrix from "@/components/SizeMatrix";
import { toast } from "sonner";

// Edit a saved shipment's pairs per size; stock and the order's shipped qty follow the difference
export default function ShipmentEditDialog({ shipment, onOpenChange, onSaved }) {
  const { productById, productLabel } = useData();
  const [sizes, setSizes] = useState({});
  const [note, setNote] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!shipment) return;
    setSizes(groupByProduct(shipment.items));
    setNote(shipment.note || "");
  }, [shipment]);

  const total = Object.values(sizes).reduce((a, m) => a + Object.values(m).reduce((x, y) => x + (parseInt(y) || 0), 0), 0);

  const save = async () => {
    const items = Object.entries(sizes).flatMap(([product_id, m]) =>
      Object.entries(m).map(([size, q]) => ({ product_id, size: parseInt(size), qty: parseInt(q) || 0 })).filter((i) => i.qty > 0));
    if (!items.length) { toast.error("Tüm adetler sıfır. Fişi kaldırmak için Sil düğmesini kullanın."); return; }
    setSaving(true);
    try {
      await api.put(`/shipments/${shipment.id}`, { note, items });
      toast.success("Sevkiyat güncellendi — stok ve sipariş düzeltildi");
      onOpenChange(false); onSaved();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Güncellenemedi");
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={!!shipment} onOpenChange={onOpenChange}>
      <DialogContent className="bg-[#FFFBF4] border-[#DCCFB8] text-[#2B2118] max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="shipment-edit-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl uppercase tracking-tight">Sevkiyatı Düzenle · {shipment?.customer_name}</DialogTitle>
        </DialogHeader>
        {Object.keys(sizes).map((pid, i) => (
          <div key={pid} className="rounded-md border border-[#DCCFB8] bg-[#EFE6D6] p-3" data-testid={`shipment-edit-product-${i}`}>
            <p className="font-mono text-sm font-bold text-[#B45309] mb-2">{productLabel(productById(pid))}</p>
            <SizeMatrix values={sizes[pid]} testIdPrefix={`shipment-edit-size-${i}`}
              onChange={(s, v) => setSizes((p) => ({ ...p, [pid]: { ...p[pid], [s]: v } }))} />
          </div>
        ))}
        <div>
          <Label className="text-xs text-[#6B5B4A]">İrsaliye Notu</Label>
          <Input data-testid="shipment-edit-note" value={note} onChange={(e) => setNote(e.target.value)} className="mt-1 h-11 bg-[#EFE6D6] border-[#DCCFB8]" />
        </div>
        <p className="text-[11px] text-[#8C7B68]">Artırılan adet stoktan ve siparişin kalanından düşer; azaltılan adet stoğa geri döner ve sipariş yeniden açılır.</p>
        <DialogFooter>
          <div className="flex items-center justify-between w-full">
            <p className="font-mono text-xl font-bold text-[#047857]" data-testid="shipment-edit-total">{total} çift</p>
            <Button onClick={save} disabled={saving} data-testid="shipment-edit-save"
              className="bg-[#D97706] hover:bg-[#B45309] text-white font-display uppercase tracking-wide h-11 px-6">
              {saving ? "Kaydediliyor..." : "Değişiklikleri Kaydet"}
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
